import {
	ChevronDoubleLeftIcon,
	ChevronDoubleRightIcon,
	ChevronDownIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	MagnifyingGlassIcon,
	PlusIcon,
} from "@heroicons/react/20/solid";
import React, { useMemo, useState } from "react";
import { connect } from "react-redux";
import AdvisorRow from "./AdvisorRow";
import { Actions as ADVISOR_ACTIONS } from "../../store/actions/advisorActions";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { DateInputFormat } from "@/common/DateComponents";

const AdvisorList = ({ advisors, createAdvisorModal, jurisdictionOptions, statusOptions, plansList }) => {
	const [search, setSearch] = useState("");
	const [planFilter, setPlanFilter] = useState(null);
	const [statusFilter, setStatusFilter] = useState(null);
	const [jurisdictionFilter, setJurisdictionFilter] = useState(null);
	const [planRenewalFrom, setPlanRenewalFrom] = useState(null);
	const [planRenewalTo, setPlanRenewalTo] = useState(new Date());
	const [complimentaryUntilFrom, setComplementaryUntilFrom] = useState(null);
	const [complimentaryUntilTo, setComplementaryUntilTo] = useState(new Date());
	const [trialExpiresFrom, setTrialExpiresFrom] = useState(null);
	const [trialExpiresTo, setTrialExpiresTo] = useState(new Date());
	const [showMarketInfo, setShowMarketInfo] = useState(false);
	const [showMore, setShowMore] = useState(false);

	const filteredAdvisorList = useMemo(() => {
		const filterByValue = (o) =>
			["id", "name", "jurisdiction", "plan", "status"].some((k) =>
				String(o[k])?.toLowerCase().includes(search.toLowerCase()),
			);

		const filterResult = search.replace(/\s/g, "") ? advisors?.filter(filterByValue) : advisors;

		return filterResult.filter(
			({ jurisdiction, plan, status, planRenewalDate, complimentaryUntil, trialExpires }) => {
				const jurisdictionMatch = jurisdictionFilter ? jurisdiction == jurisdictionFilter : true;
				const planMatch = planFilter ? plan == planFilter : true;
				const statusMatch = statusFilter ? status == statusFilter : true;
				const planRenewalMatch =
					planRenewalFrom && planRenewalTo
						? planRenewalFrom <= new Date(planRenewalDate) && new Date(planRenewalDate) <= planRenewalTo
						: true;
				const complimentaryUntilMatch =
					complimentaryUntilFrom && complimentaryUntilTo
						? complimentaryUntilFrom <= new Date(complimentaryUntil) &&
							new Date(complimentaryUntil) <= complimentaryUntilTo
						: true;
				const trialExpiresMatch =
					trialExpiresFrom && trialExpiresTo
						? trialExpiresFrom <= new Date(trialExpires) && new Date(trialExpires) <= trialExpiresTo
						: true;
				return (
					jurisdictionMatch &&
					planMatch &&
					statusMatch &&
					planRenewalMatch &&
					complimentaryUntilMatch &&
					trialExpiresMatch
				);
			},
		);
	}, [
		search,
		advisors,
		jurisdictionFilter,
		planFilter,
		statusFilter,
		planRenewalFrom,
		planRenewalTo,
		complimentaryUntilFrom,
		complimentaryUntilTo,
		trialExpiresFrom,
		trialExpiresTo,
	]);

	const [itemsPerPage, setItemsPerPage] = useState(20);
	const [page, setPage] = useState(0);
	const start = page * itemsPerPage;
	const end = (page + 1) * itemsPerPage;
	const lastPage = Math.ceil(filteredAdvisorList?.length / itemsPerPage);

	const Pagination = () => {
		if (filteredAdvisorList?.length <= 0) return null;
		return (
			<thead>
				<tr>
					<th colSpan={20}>
						<div
							style={{
								display: "grid",
								gridGap: "1.5rem",
								grid: "1.5rem",
								gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
							}}
						>
							<div style={{ gridColumn: "span 1 / span 3", textAlign: "center" }}>
								<button
									className="btn btn-xs btn-default pull-left"
									title="Go To Page 1"
									style={{ marginLeft: "0.5rem" }}
									onClick={() => {
										if (page != 0) setPage(0);
									}}
									disabled={page === 0}
								>
									<ChevronDoubleLeftIcon style={{ height: "1.5em", float: "left" }} />
								</button>
								<button
									className="btn btn-xs btn-default pull-left"
									title="Go Back 5 Pages"
									style={{ marginLeft: "0.5rem" }}
									onClick={() => {
										if (page != 0) setPage(Math.max(page - 5, 0));
									}}
									disabled={page === 0}
								>
									<ChevronDoubleLeftIcon style={{ height: "1.5em", float: "left" }} />
								</button>
								<button
									className="btn btn-xs btn-default pull-left"
									title="Go Back 1 Page"
									style={{ marginLeft: "0.5rem" }}
									onClick={() => {
										if (page != 0) setPage(page - 1);
									}}
									disabled={page === 0}
								>
									<ChevronLeftIcon style={{ height: "1.5em", float: "left" }} />
								</button>
							</div>
							<div style={{ gridColumn: "span 1 / span 3", textAlign: "center" }}>
								<strong>
									Page {page + 1}/{lastPage}
								</strong>
							</div>
							<div style={{ gridColumn: "span 1 / span 3", textAlign: "center" }}>
								<button
									className="btn btn-xs btn-default pull-right"
									style={{ marginRight: "0.5rem" }}
									title={`Go to Page ${lastPage}`}
									onClick={() => {
										if (page !== lastPage - 1) setPage(lastPage - 1);
									}}
									disabled={page === lastPage - 1}
								>
									<ChevronDoubleRightIcon style={{ height: "1.5em", float: "left" }} />
								</button>
								<button
									className="btn btn-xs btn-default pull-right"
									style={{ marginRight: "0.5rem" }}
									title="Go Forward 5 Pages"
									onClick={() => {
										if (page !== lastPage - 1) setPage(Math.min(page + 5, lastPage - 1));
									}}
									disabled={page === lastPage - 1}
								>
									<ChevronDoubleRightIcon style={{ height: "1.5em", float: "left" }} />
								</button>
								<button
									className="btn btn-xs btn-default pull-right"
									title="Go Forward 1 Page"
									onClick={() => {
										if (page !== lastPage - 1) setPage(page + 1);
									}}
									style={{ marginRight: "0.5rem" }}
									disabled={page === lastPage - 1}
								>
									<ChevronRightIcon style={{ height: "1.5em", float: "left" }} />
								</button>
							</div>
						</div>
					</th>
				</tr>
			</thead>
		);
	};

	return (
		<>
			<div className="content bg-gray-lighter">
				<div className="row">
					<div className="col-sm-12">
						<div className="pull-left">
							<h1 className="page-heading">Advisor List</h1>
						</div>
						<div
							className="btn btn-success pull-right"
							style={{ marginLeft: 10 }}
							onClick={() => createAdvisorModal(true)}
						>
							<PlusIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
							<div style={{ float: "left" }}>Create New Advisor</div>
						</div>
					</div>
				</div>
			</div>

			<div
				className="content"
				style={{ overflow: "show" }}
			>
				<div className="block-header pb-0">
					<div className="col-xs-2 pl-0">
						<label for="Plan">Plan</label>
						<Select
							options={plansList}
							isClearable
							onChange={(e) => {
								setPlanFilter(e?.value || null);
							}}
						/>
					</div>
					<div className="col-xs-2 pl-0">
						<label for="Status">Status</label>
						<Select
							options={statusOptions}
							isClearable
							onChange={(e) => {
								setStatusFilter(e?.value || null);
							}}
						/>
					</div>
					<div className="col-xs-2 pl-0">
						<label for="Jurisdiction">Jurisdiction</label>
						<Select
							options={jurisdictionOptions}
							onChange={(e) => {
								setJurisdictionFilter(e?.value || null);
							}}
							isClearable
						/>
					</div>
					<div className="col-xs-2 pl-0">
						<label>Show Marketing Info</label>
						<label className="css-input switch switch-sm switch-success mt-4">
							<input
								type="checkbox"
								checked={showMarketInfo}
								onChange={(e) => {
									setShowMarketInfo(e.target.checked);
								}}
							/>
							<span />
						</label>
					</div>
				</div>
				<div className="col-xs-12 pl-3">
					<div
						style={{
							fontSize: 11,
							padding: "3px 6px",
							float: "left",
							color: "#ea611c",
							cursor: "pointer",
						}}
						onClick={() => {
							setShowMore(!showMore);
						}}
					>
						<ChevronDownIcon
							style={{
								minWidth: "1.5rem",
								width: "1.5rem",
								marginLeft: 5,
								verticalAlign: "text-bottom",
								cursor: "pointer",
								display: "inline-block",
								textAlign: "center",
								transform: showMore ? "rotateX(180deg)" : "rotateX(0deg)",
								transition: "transform 0.3s ease-in-out",
							}}
						/>
						{showMore ? "Less Options" : "More Options"}
					</div>
				</div>
				{showMore && (
					<div className="block-header pb-0">
						<div className="col-xs-12 p-0">
							<div className="col-xs-2 pl-0">
								<label for="planRenewalDate">Plan Renewal Date</label>
								<DatePicker
									customInput={<DateInputFormat />}
									selected={planRenewalFrom}
									onChange={(date) => {
										setPlanRenewalFrom(date);
									}}
									maxDate={planRenewalTo || new Date()}
									wrapperClassName="full-width"
								/>
							</div>
							<div
								className="col-xs-1 p-0 mt-4"
								style={{ fontSize: "40px", width: "2.5rem" }}
							>
								-
							</div>
							<div className="col-xs-2 pl-0 mt-9">
								<DatePicker
									customInput={<DateInputFormat />}
									selected={planRenewalTo}
									onChange={(date) => {
										setPlanRenewalTo(date);
									}}
									minDate={planRenewalFrom || null}
									maxDate={new Date()}
									wrapperClassName="full-width"
								/>
							</div>
							<div
								className="col-xs-1"
								style={{ width: "10px" }}
							/>
							<div className="col-xs-2 pl-0">
								<label for="complimentaryUntilDate">Complimentary Until</label>
								<DatePicker
									customInput={<DateInputFormat />}
									selected={complimentaryUntilFrom}
									onChange={(date) => {
										setComplementaryUntilFrom(date);
									}}
									maxDate={complimentaryUntilTo || new Date()}
									wrapperClassName="full-width"
								/>
							</div>
							<div
								className="col-xs-1 p-0 mt-4"
								style={{ fontSize: "40px", width: "2.5rem" }}
							>
								-
							</div>
							<div className="col-xs-2 pl-0 mt-9">
								<DatePicker
									customInput={<DateInputFormat />}
									selected={complimentaryUntilTo}
									onChange={(date) => {
										setComplementaryUntilTo(date);
									}}
									minDate={complimentaryUntilFrom || null}
									maxDate={new Date()}
									wrapperClassName="full-width"
								/>
							</div>
						</div>
						<div className="col-xs-12 p-0">
							<div className="col-xs-2 pl-0">
								<label for="trialExpiresDate">Trial Expires</label>
								<DatePicker
									customInput={<DateInputFormat />}
									selected={trialExpiresFrom}
									onChange={(date) => {
										setTrialExpiresFrom(date);
									}}
									maxDate={trialExpiresTo || new Date()}
									wrapperClassName="full-width"
								/>
							</div>
							<div
								className="col-xs-1 p-0 mt-4"
								style={{ fontSize: "40px", width: "2.5rem" }}
							>
								-
							</div>
							<div className="col-xs-2 pl-0 mt-9">
								<DatePicker
									customInput={<DateInputFormat />}
									selected={trialExpiresTo}
									onChange={(date) => {
										setTrialExpiresTo(date);
									}}
									minDate={trialExpiresFrom || null}
									maxDate={new Date()}
									wrapperClassName="full-width"
								/>
							</div>
						</div>
					</div>
				)}
				<div className="clearfix" />
				<div
					className="block-content"
					style={{ minHeight: "fit-content" }}
				>
					<div
						style={{
							border: "1px solid #bbbcbc",
							borderBottom: "none",
							position: "relative",
						}}
					>
						<MagnifyingGlassIcon
							className={"text-gray"}
							style={{
								height: "1.5em",
								position: "absolute",
								margin: "0.75em",
							}}
						></MagnifyingGlassIcon>
						<input
							name="search"
							onChange={(e) => setSearch(e.target.value)}
							value={search}
							style={{
								border: "none",
								display: "inline-block",
								maxWidth: "100%",
								width: "100%",
								padding: 10,
								paddingLeft: 40,
							}}
						/>
					</div>
					<div style={{ overflowX: "auto", maxWidth: "100%" }}>
						<table
							id="table-timesheets"
							className="table-responsive table table-hover"
						>
							<Pagination />
							<thead>
								<tr>
									<th width="1%">Name</th>
									<th
										style={{
											textAlign: "center",
										}}
									>
										Plan Details
									</th>
									<th style={{ textAlign: "center" }}>Current Status</th>
									<th style={{ textAlign: "center" }}>Jurisdiction</th>
									<th style={{ textAlign: "center", display: showMarketInfo ? "" : "none" }}>
										Employees
									</th>
									<th style={{ textAlign: "center", display: showMarketInfo ? "" : "none" }}>
										Payrolls
									</th>
									<th style={{ textAlign: "center", display: showMarketInfo ? "" : "none" }}>
										Software
									</th>
									<th
										width="5%"
										style={{ display: showMarketInfo ? "none" : "" }}
									/>
									<th width="5%" />
								</tr>
							</thead>
							<tbody>
								{filteredAdvisorList?.slice(start, end)?.map((advisor) => (
									<AdvisorRow
										key={advisor.id}
										advisor={advisor}
										showMarketInfo={showMarketInfo}
									/>
								)) || (
									<tr>
										<th>You have no advisors.</th>
									</tr>
								)}
							</tbody>
							<Pagination />
						</table>
					</div>
				</div>
				<div className="clearfix" />
			</div>
		</>
	);
};

const mapStateToProps = (state) => {
	const statusOptions = [
		{ value: "Active", label: "Active" },
		{ value: "Archived", label: "Archived" },
		{ value: "Suspended", label: "Suspended" },
		{ value: "Setup", label: "Setup" },
		{ value: "Demo", label: "Demo" },
		{ value: "Test", label: "Test" },
		{ value: "Complimentary", label: "Complimentary" },
	];

	const jurisdictions = state?.jurisdictions || [];

	const jurisdictionOptions = jurisdictions.map((jurisdiction) => ({
		label: jurisdiction?.name,
		value: jurisdiction?.countryCode,
	}));

	return {
		advisors: state?.advisors?.advisorList || [],
		statusOptions,
		jurisdictionOptions,
		plansList: state?.payment?.bundles
			?.filter(({ isPartnerPlan }) => isPartnerPlan)
			?.map(({ id, name }) => ({ value: id, label: name })),
	};
};

const mapDispatchToProps = (dispatch) => ({
	createAdvisorModal: (status) => dispatch(ADVISOR_ACTIONS.createAdvisorModal(status)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdvisorList);
